/**
 * Counts how many of `fields` differ from `defaults`. Array-valued fields are
 * compared by length — an empty array equals an undefined default ("nothing
 * selected") — while scalar fields use strict inequality.
 *
 * This drives the active-filter count badge on every Filters popover: the badge
 * answers "how many of the popover's controls have I moved off their defaults?",
 * so each surface passes exactly the fields its popover exposes.
 */
export function activeFilterCount<T>(
  filters: T,
  defaults: T,
  fields: readonly (keyof T)[]
): number {
  return fields.reduce((count, field) => {
    const value = filters[field];
    const def = defaults[field];
    const differs =
      Array.isArray(value) || Array.isArray(def)
        ? ((value as unknown[] | undefined) ?? []).length !==
          ((def as unknown[] | undefined) ?? []).length
        : value !== def;

    return differs ? count + 1 : count;
  }, 0);
}

/**
 * Returns a copy of `filters` with every field in `fields` reset to its value in
 * `defaults`, leaving all other fields untouched. Drives "Clear all" on Filters
 * popovers — it resets exactly the controls the badge counts, so report-scope
 * fields a page does not surface in its popover survive.
 */
export function resetFilters<T>(filters: T, defaults: T, fields: readonly (keyof T)[]): T {
  const next = { ...filters };
  for (const field of fields) {
    next[field] = defaults[field];
  }

  return next;
}
